import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, CheckIcon, SendIcon, LinkedinIcon, TwitterIcon, FacebookIcon, InstagramIcon } from 'lucide-react';
export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Veuillez entrer votre nom';
    if (!formData.email.trim()) {
      newErrors.email = 'Veuillez entrer votre email';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }
    if (!formData.subject) newErrors.subject = 'Veuillez sélectionner un sujet';
    if (!formData.message.trim()) newErrors.message = 'Veuillez entrer votre message';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      // Success
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: ''
      });
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-white text-gray-900 font-sans" data-id="element-1835">
      <Navbar data-id="element-1836" />
      <main className="pt-20" data-id="element-1837">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-20 relative overflow-hidden" data-id="element-1838">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10" data-id="element-1839">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-1840">
              <defs data-id="element-1841">
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-1842">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" data-id="element-1843" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" data-id="element-1844" />
            </svg>
          </div>
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-blue-800 rounded-full opacity-30 transform translate-x-1/3 -translate-y-1/3" data-id="element-1845"></div>
          <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-yellow-500 rounded-full opacity-20 transform -translate-x-1/3 translate-y-1/3" data-id="element-1846"></div>
          <div className="container mx-auto px-4 relative z-10" data-id="element-1847">
            <div className="max-w-3xl mx-auto text-center" data-id="element-1848">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-1849">
                Contactez-nous
              </h1>
              <p className="text-xl text-blue-100 mb-8" data-id="element-1850">
                Notre équipe d'experts est à votre disposition pour répondre à
                toutes vos questions et vous accompagner dans votre
                développement en Afrique.
              </p>
            </div>
          </div>
        </section>
        {/* Contact Info Cards */}
        <section className="py-16 bg-white relative" data-id="element-1851">
          <div className="container mx-auto px-4" data-id="element-1852">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-24 relative z-10" data-id="element-1853">
              <motion.div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} viewport={{
              once: true
            }} data-id="element-1854">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-1855">
                  <MapPinIcon size={20} className="text-blue-700" data-id="element-1856" />
                </div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2" data-id="element-1857">
                  Notre adresse
                </h3>
                <p className="text-gray-600" data-id="element-1858">
                  12 Rue de la Bourse
                  <br data-id="element-1859" />
                  75002 Paris, France
                </p>
              </motion.div>
              <motion.div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.1
            }} viewport={{
              once: true
            }} data-id="element-1860">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-1861">
                  <PhoneIcon size={20} className="text-blue-700" data-id="element-1862" />
                </div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2" data-id="element-1863">
                  Téléphone
                </h3>
                <p className="text-gray-600" data-id="element-1864">
                  +33 7 53 29 34 19
                  <br data-id="element-1865" />
                  +33 1 42 68 53 91
                </p>
              </motion.div>
              <motion.div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.2
            }} viewport={{
              once: true
            }} data-id="element-1866">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-1867">
                  <MailIcon size={20} className="text-blue-700" data-id="element-1868" />
                </div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2" data-id="element-1869">
                  Email
                </h3>
                <p className="text-gray-600" data-id="element-1870">
                  contact@elevateit.fr
                  <br data-id="element-1871" />
                  support@elevateit.fr
                </p>
              </motion.div>
              <motion.div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.3
            }} viewport={{
              once: true
            }} data-id="element-1872">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-1873">
                  <ClockIcon size={20} className="text-blue-700" data-id="element-1874" />
                </div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2" data-id="element-1875">
                  Heures d'ouverture
                </h3>
                <p className="text-gray-600" data-id="element-1876">
                  Lun - Ven: 9h - 18h
                  <br data-id="element-1877" />
                  Fermé le weekend
                </p>
              </motion.div>
            </div>
          </div>
        </section>
        {/* Contact Form & Map Section */}
        <section className="py-16 bg-gray-50" data-id="element-1878">
          <div className="container mx-auto px-4" data-id="element-1879">
            <div className="max-w-6xl mx-auto" data-id="element-1880">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1881">
                {/* Contact Form */}
                <motion.div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100" initial={{
                opacity: 0,
                x: -30
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5
              }} viewport={{
                once: true
              }} data-id="element-1882">
                  <h2 className="text-2xl font-bold text-blue-900 mb-6" data-id="element-1883">
                    Envoyez-nous un message
                  </h2>
                  {submitSuccess && <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start" data-id="element-1884">
                      <CheckIcon size={20} className="text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1885" />
                      <p className="text-green-800" data-id="element-1886">
                        Votre message a été envoyé avec succès. Nous vous
                        répondrons dans les plus brefs délais.
                      </p>
                    </div>}
                  <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1887">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1888">
                      <div data-id="element-1889">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1890">
                          Nom complet <span className="text-red-500" data-id="element-1891">*</span>
                        </label>
                        <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`block w-full px-4 py-3 border ${errors.name ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`} data-id="element-1892" />
                        {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-1893">
                            {errors.name}
                          </p>}
                      </div>
                      <div data-id="element-1894">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1895">
                          Email <span className="text-red-500" data-id="element-1896">*</span>
                        </label>
                        <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`block w-full px-4 py-3 border ${errors.email ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`} data-id="element-1897" />
                        {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-1898">
                            {errors.email}
                          </p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1899">
                      <div data-id="element-1900">
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1901">
                          Entreprise
                        </label>
                        <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className="block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-1902" />
                      </div>
                      <div data-id="element-1903">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1904">
                          Téléphone
                        </label>
                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-1905" />
                      </div>
                    </div>
                    <div data-id="element-1906">
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1907">
                        Sujet <span className="text-red-500" data-id="element-1908">*</span>
                      </label>
                      <select id="subject" name="subject" value={formData.subject} onChange={handleChange} className={`block w-full px-4 py-3 border ${errors.subject ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`} data-id="element-1909">
                        <option value="" data-id="element-1910">Sélectionnez un sujet</option>
                        <option value="information" data-id="element-1911">
                          Demande d'information
                        </option>
                        <option value="demo" data-id="element-1912">Demande de démonstration</option>
                        <option value="partnership" data-id="element-1913">Partenariat</option>
                        <option value="support" data-id="element-1914">Support technique</option>
                        <option value="other" data-id="element-1915">Autre</option>
                      </select>
                      {errors.subject && <p className="mt-1 text-sm text-red-600" data-id="element-1916">
                          {errors.subject}
                        </p>}
                    </div>
                    <div data-id="element-1917">
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1918">
                        Message <span className="text-red-500" data-id="element-1919">*</span>
                      </label>
                      <textarea id="message" name="message" rows={5} value={formData.message} onChange={handleChange} className={`block w-full px-4 py-3 border ${errors.message ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`} data-id="element-1920"></textarea>
                      {errors.message && <p className="mt-1 text-sm text-red-600" data-id="element-1921">
                          {errors.message}
                        </p>}
                    </div>
                    <div className="flex items-center" data-id="element-1922">
                      <input id="privacy" name="privacy" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-1923" />
                      <label htmlFor="privacy" className="ml-2 block text-sm text-gray-700" data-id="element-1924">
                        J'accepte la{' '}
                        <a href="#" className="text-blue-600 hover:underline" data-id="element-1925">
                          politique de confidentialité
                        </a>
                      </label>
                    </div>
                    <div data-id="element-1926">
                      <Button type="submit" variant="primary" disabled={isSubmitting} className="shadow-lg shadow-yellow-500/20 flex items-center justify-center" fullWidth data-id="element-1927">
                        {isSubmitting ? 'Envoi en cours...' : <>
                            <span data-id="element-1928">Envoyer le message</span>
                            <SendIcon size={16} className="ml-2" data-id="element-1929" />
                          </>}
                      </Button>
                    </div>
                  </form>
                </motion.div>
                {/* Map */}
                <motion.div className="h-full min-h-[400px] bg-gray-200 rounded-xl overflow-hidden shadow-lg relative" initial={{
                opacity: 0,
                x: 30
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5
              }} viewport={{
                once: true
              }} data-id="element-1930">
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.2463490130985!2d2.3394836157117287!3d48.869918079288624!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e3ec70bdb33%3A0x5743eee2e28e8a85!2s12%20Rue%20de%20la%20Bourse%2C%2075002%20Paris%2C%20France!5e0!3m2!1sen!2sus!4v1620298972548!5m2!1sen!2sus" width="100%" height="100%" style={{
                  border: 0
                }} allowFullScreen loading="lazy" title="ElevateIT Office Location" data-id="element-1931"></iframe>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
        {/* FAQ Section */}
        <section className="py-16 bg-white" data-id="element-1932">
          <div className="container mx-auto px-4" data-id="element-1933">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1934">
              <span className="inline-block px-3 py-1 bg-blue-50 text-blue-900 text-sm font-semibold rounded-full mb-4" data-id="element-1935">
                FAQ
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6" data-id="element-1936">
                Questions fréquentes
              </h2>
              <p className="text-lg text-gray-700" data-id="element-1937">
                Vous avez des questions ? Consultez notre FAQ ou contactez-nous
                directement.
              </p>
            </div>
            <div className="max-w-4xl mx-auto" data-id="element-1938">
              <div className="space-y-6" data-id="element-1939">
                {[{
                question: "Comment fonctionne l'essai gratuit de 7 jours ?",
                answer: "L'essai gratuit vous donne un accès complet à toutes les fonctionnalités de notre plateforme pendant 7 jours. Aucune carte bancaire n'est requise et vous pouvez annuler à tout moment. À la fin de la période d'essai, vous pouvez choisir l'abonnement qui correspond le mieux à vos besoins."
              }, {
                question: 'Quels pays sont couverts par votre plateforme ?',
                answer: "Notre plateforme couvre plus de 30 pays africains, dont le Maroc, l'Algérie, la Tunisie, l'Égypte, le Sénégal, la Côte d'Ivoire, le Nigeria, le Kenya, l'Éthiopie, l'Afrique du Sud et bien d'autres. Nous ajoutons régulièrement de nouveaux pays à notre couverture."
              }, {
                question: "Comment sont vérifiées les informations sur les appels d'offres ?",
                answer: "Toutes les informations sont collectées et vérifiées par notre équipe d'experts locaux dans chaque pays. Nous utilisons également des technologies avancées pour assurer la qualité et la fraîcheur des données. Chaque appel d'offres est vérifié et enrichi avant d'être publié sur notre plateforme."
              }, {
                question: 'Proposez-vous un accompagnement personnalisé ?',
                answer: "Oui, nous proposons un accompagnement personnalisé pour les entreprises qui souhaitent maximiser leurs chances de succès. Notre équipe d'experts peut vous aider à identifier les opportunités les plus pertinentes, à préparer vos dossiers de candidature et à vous mettre en relation avec des partenaires locaux."
              }].map((faq, index) => <motion.div key={index} className="bg-gray-50 rounded-lg p-6 shadow-sm" initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: index * 0.1
              }} viewport={{
                once: true
              }} data-id="element-1940">
                    <h3 className="text-lg font-semibold text-blue-900 mb-3" data-id="element-1941">
                      {faq.question}
                    </h3>
                    <p className="text-gray-700" data-id="element-1942">{faq.answer}</p>
                  </motion.div>)}
              </div>
              <div className="mt-12 text-center" data-id="element-1943">
                <p className="text-gray-700 mb-4" data-id="element-1944">
                  Vous n'avez pas trouvé de réponse à votre question ?
                </p>
                <Button variant="secondary" className="flex items-center mx-auto" onClick={() => {
                // Scroll to contact form
                document.getElementById('contact-form')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }} data-id="element-1945">
                  <PhoneIcon size={16} className="mr-2" data-id="element-1946" />
                  <span data-id="element-1947">Contactez-nous directement</span>
                </Button>
              </div>
            </div>
          </div>
        </section>
        {/* Social Media Section */}
        <section className="py-16 bg-blue-900 text-white relative overflow-hidden" data-id="element-1948">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10" data-id="element-1949">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-1950">
              <defs data-id="element-1951">
                <pattern id="smallGrid" width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-1952">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="white" strokeWidth="0.5" data-id="element-1953" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#smallGrid)" data-id="element-1954" />
            </svg>
          </div>
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-800 rounded-full opacity-50 transform translate-x-1/3 -translate-y-1/3" data-id="element-1955"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-yellow-500 rounded-full opacity-20 transform -translate-x-1/3 translate-y-1/3" data-id="element-1956"></div>
          <div className="container mx-auto px-4 relative z-10" data-id="element-1957">
            <div className="max-w-3xl mx-auto text-center" data-id="element-1958">
              <h2 className="text-3xl font-bold mb-6" data-id="element-1959">
                Suivez-nous sur les réseaux sociaux
              </h2>
              <p className="text-blue-100 mb-8" data-id="element-1960">
                Restez informé des dernières opportunités et actualités du
                marché africain en nous suivant sur les réseaux sociaux.
              </p>
              <div className="flex justify-center space-x-4" data-id="element-1961">
                <a href="#" className="w-12 h-12 rounded-full bg-blue-800 flex items-center justify-center hover:bg-blue-700 transition-colors" data-id="element-1962">
                  <LinkedinIcon size={24} data-id="element-1963" />
                </a>
                <a href="#" className="w-12 h-12 rounded-full bg-blue-800 flex items-center justify-center hover:bg-blue-700 transition-colors" data-id="element-1964">
                  <TwitterIcon size={24} data-id="element-1965" />
                </a>
                <a href="#" className="w-12 h-12 rounded-full bg-blue-800 flex items-center justify-center hover:bg-blue-700 transition-colors" data-id="element-1966">
                  <FacebookIcon size={24} data-id="element-1967" />
                </a>
                <a href="#" className="w-12 h-12 rounded-full bg-blue-800 flex items-center justify-center hover:bg-blue-700 transition-colors" data-id="element-1968">
                  <InstagramIcon size={24} data-id="element-1969" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-1970" />
    </div>;
}